import {
  ZadReplenishmentCoverageStatus,
  ZadReplenishmentOpenOrder,
  ZadReplenishmentPhysicalStatus,
  ZadReplenishmentProductDetail,
  ZadReplenishmentRow,
  ZadReplenishmentSelectionItem,
  ZadReplenishmentSettingsPayload,
  ZadReplenishmentSummary,
} from '../../../models/zad'

/**
 * currentStock = physical stock now.
 * onOrderQuantity = quantity covered by active purchase-order workflow.
 * projectedStock = currentStock + onOrderQuantity (backend).
 * suggestedQuantity = targetStock - physical current stock (gross need).
 * remainingToOrder = targetStock - projectedStock (actionable remaining need).
 * Physical status and coverage status are independent.
 */

export const ZAD_REPLENISHMENT_PHYSICAL_STATUSES: ZadReplenishmentPhysicalStatus[] = [
  'OUT_OF_STOCK',
  'CRITICAL',
  'LOW',
  'OK',
  'DISABLED',
]

export const ZAD_REPLENISHMENT_COVERAGE_STATUSES: ZadReplenishmentCoverageStatus[] = [
  'NOT_ORDERED',
  'PARTIALLY_COVERED',
  'COVERED',
  'NOT_NEEDED',
]

export const ZAD_REPLENISHMENT_PHYSICAL_I18N: Record<ZadReplenishmentPhysicalStatus, string> = {
  OUT_OF_STOCK: 'ZAD.REPLENISHMENT.STATUS_OUT',
  CRITICAL: 'ZAD.REPLENISHMENT.STATUS_CRITICAL',
  LOW: 'ZAD.REPLENISHMENT.STATUS_LOW',
  OK: 'ZAD.REPLENISHMENT.STATUS_OK',
  DISABLED: 'ZAD.REPLENISHMENT.STATUS_DISABLED',
}

export const ZAD_REPLENISHMENT_COVERAGE_I18N: Record<ZadReplenishmentCoverageStatus, string> = {
  NOT_ORDERED: 'ZAD.REPLENISHMENT.COVER_NOT_ORDERED',
  PARTIALLY_COVERED: 'ZAD.REPLENISHMENT.COVER_PARTIAL',
  COVERED: 'ZAD.REPLENISHMENT.COVER_COVERED',
  NOT_NEEDED: 'ZAD.REPLENISHMENT.COVER_NOT_NEEDED',
}

export const ZAD_REPLENISHMENT_PHYSICAL_BADGE: Record<ZadReplenishmentPhysicalStatus, string> = {
  OUT_OF_STOCK: 'badge-light-danger',
  CRITICAL: 'badge-light-warning',
  LOW: 'badge-light-info',
  OK: 'badge-light-success',
  DISABLED: 'badge-light-secondary',
}

export const ZAD_REPLENISHMENT_COVERAGE_BADGE: Record<ZadReplenishmentCoverageStatus, string> = {
  NOT_ORDERED: 'badge-light-danger',
  PARTIALLY_COVERED: 'badge-light-warning',
  COVERED: 'badge-light-success',
  NOT_NEEDED: 'badge-light-secondary',
}

export const ZAD_REPLENISHMENT_SORT_OPTIONS = [
  {value: '', i18nId: 'ZAD.REPLENISHMENT.SORT_URGENCY'},
  {value: 'currentStock', i18nId: 'ZAD.REPLENISHMENT.CURRENT_STOCK'},
  {value: 'onOrderQuantity', i18nId: 'ZAD.REPLENISHMENT.ON_ORDER'},
  {value: 'projectedStock', i18nId: 'ZAD.REPLENISHMENT.PROJECTED_STOCK'},
  {value: 'remainingToOrder', i18nId: 'ZAD.REPLENISHMENT.REMAINING'},
  {value: 'name', i18nId: 'ZAD.REPLENISHMENT.PRODUCT'},
  {value: 'code', i18nId: 'ZAD.REPLENISHMENT.CODE'},
] as const

export interface ZadReplenishmentQuery {
  search?: string
  status?: string
  coverageStatus?: string
  needsOrdering?: boolean
  includeDisabled?: boolean
  pageNumber: number
  pageCount: number
  sortField?: string
  sortOrder?: string
}

export const DEFAULT_ZAD_REPLENISHMENT_QUERY: ZadReplenishmentQuery = {
  search: '',
  status: '',
  coverageStatus: '',
  needsOrdering: false,
  includeDisabled: false,
  pageNumber: 1,
  pageCount: 10,
  sortField: '',
  sortOrder: '',
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function firstDefined(...values: unknown[]): unknown {
  return values.find((value) => value !== undefined && value !== null)
}

function asText(value: unknown): string {
  return value == null ? '' : String(value).trim()
}

function asFiniteNumber(value: unknown): number | undefined {
  if (value === '' || value == null) return undefined
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

function asNumberOrZero(value: unknown): number {
  return asFiniteNumber(value) ?? 0
}

function asNullableNumber(value: unknown): number | null {
  const numeric = asFiniteNumber(value)
  return numeric === undefined ? null : numeric
}

function asPhysicalStatus(value: unknown): ZadReplenishmentPhysicalStatus {
  if (
    value === 'OUT_OF_STOCK' ||
    value === 'CRITICAL' ||
    value === 'LOW' ||
    value === 'OK' ||
    value === 'DISABLED'
  ) {
    return value
  }
  return 'OK'
}

function asCoverageStatus(value: unknown): ZadReplenishmentCoverageStatus {
  if (
    value === 'NOT_ORDERED' ||
    value === 'PARTIALLY_COVERED' ||
    value === 'COVERED' ||
    value === 'NOT_NEEDED'
  ) {
    return value
  }
  return 'NOT_NEEDED'
}

export function buildZadReplenishmentQuery(params: ZadReplenishmentQuery): string {
  const query = new URLSearchParams()
  query.set('pageNumber', String(params.pageNumber > 0 ? params.pageNumber : 1))
  query.set('pageCount', String(params.pageCount > 0 ? params.pageCount : 10))
  if (params.search?.trim()) query.set('search', params.search.trim())
  if (params.status?.trim()) query.set('status', params.status.trim())
  if (params.coverageStatus?.trim()) query.set('coverageStatus', params.coverageStatus.trim())
  if (params.needsOrdering) query.set('needsOrdering', 'true')
  if (params.includeDisabled) query.set('includeDisabled', 'true')
  if (params.sortField?.trim() && params.sortField !== 'urgency') {
    query.set('sortField', params.sortField.trim())
    query.set('sortOrder', params.sortOrder === 'asc' ? 'asc' : 'desc')
  }
  return query.toString()
}

export function extractZadReplenishmentRow(value: unknown): ZadReplenishmentRow | null {
  const raw = asRecord(value)
  const product = asRecord(raw.product)
  const productId = asText(firstDefined(raw.productId, raw._id, product._id, product.id))
  if (!productId) return null
  return {
    productId,
    code: asText(firstDefined(raw.code, product.code)),
    barcode: asText(firstDefined(raw.barcode, product.barcode)),
    name: asText(firstDefined(raw.name, product.name)),
    currentStock: asNumberOrZero(raw.currentStock),
    minimumStock: asNullableNumber(raw.minimumStock),
    reorderPoint: asNullableNumber(raw.reorderPoint),
    targetStock: asNullableNumber(raw.targetStock),
    status: asPhysicalStatus(raw.status),
    suggestedQuantity: asNumberOrZero(raw.suggestedQuantity),
    onOrderQuantity: asNumberOrZero(raw.onOrderQuantity),
    projectedStock: asNumberOrZero(raw.projectedStock),
    remainingToOrder: asNumberOrZero(raw.remainingToOrder),
    coverageStatus: asCoverageStatus(raw.coverageStatus),
    isEnabled: raw.isEnabled !== false && asPhysicalStatus(raw.status) !== 'DISABLED',
  }
}

function emptySummary(): ZadReplenishmentSummary {
  return {
    enabledProducts: 0,
    outOfStock: 0,
    critical: 0,
    low: 0,
    ok: 0,
    needsReplenishment: 0,
    notOrdered: 0,
    partiallyCovered: 0,
    covered: 0,
    needsOrderingNow: 0,
  }
}

export function extractZadReplenishmentSummary(value: unknown): ZadReplenishmentSummary {
  const raw = asRecord(value)
  return {
    enabledProducts: asNumberOrZero(raw.enabledProducts),
    outOfStock: asNumberOrZero(raw.outOfStock),
    critical: asNumberOrZero(raw.critical),
    low: asNumberOrZero(raw.low),
    ok: asNumberOrZero(raw.ok),
    needsReplenishment: asNumberOrZero(raw.needsReplenishment),
    notOrdered: asNumberOrZero(raw.notOrdered),
    partiallyCovered: asNumberOrZero(raw.partiallyCovered),
    covered: asNumberOrZero(raw.covered),
    needsOrderingNow: asNumberOrZero(raw.needsOrderingNow),
  }
}

export function extractZadReplenishmentList(data: unknown): {
  items: ZadReplenishmentRow[]
  total: number
  totalPages: number
  summary: ZadReplenishmentSummary
} {
  const raw = asRecord(data)
  const inner = asRecord(raw.data)
  const payload = inner.items || inner.products || inner.summary ? inner : raw
  const list = payload.items || payload.products
  const items = Array.isArray(list)
    ? list.map(extractZadReplenishmentRow).filter((row): row is ZadReplenishmentRow => Boolean(row))
    : []
  return {
    items,
    total: asNumberOrZero(payload.total),
    totalPages: asNumberOrZero(payload.totalPages),
    summary: extractZadReplenishmentSummary(payload.summary),
  }
}

function extractOpenOrder(value: unknown, index: number): ZadReplenishmentOpenOrder | null {
  const raw = asRecord(value)
  const number = asText(
    firstDefined(
      raw.number,
      raw.purchaseOrderNumber,
      raw.documentNumber,
      raw.reference,
      raw.zbcNumber,
      raw.code
    )
  )
  if (!number) return null
  return {
    id: asText(firstDefined(raw._id, raw.id)) || `${number}-${index}`,
    number,
    quantity: asNumberOrZero(firstDefined(raw.quantity, raw.onOrderQuantity)),
    status: asText(raw.status) || '—',
  }
}

export function extractZadReplenishmentDetail(data: unknown): ZadReplenishmentProductDetail | null {
  const raw = asRecord(data)
  const inner = asRecord(raw.data)
  const payload = inner.productId || inner.product || inner.currentStock != null ? inner : raw
  const row = extractZadReplenishmentRow(payload)
  if (!row) return null
  const ordersRaw = firstDefined(payload.openOrders, payload.contributingOrders)
  const openOrders = Array.isArray(ordersRaw)
    ? ordersRaw
        .map(extractOpenOrder)
        .filter((order): order is ZadReplenishmentOpenOrder => Boolean(order))
        .slice(0, 20)
    : []
  return {
    ...row,
    openOrders,
    openOrdersTruncated: payload.openOrdersTruncated === true,
  }
}

export function isZadReplenishmentSelectable(row: ZadReplenishmentRow): boolean {
  if (row.coverageStatus === 'COVERED' || row.coverageStatus === 'NOT_NEEDED') return false
  return row.remainingToOrder > 0
}

export function toZadReplenishmentSelectionItem(row: ZadReplenishmentRow): ZadReplenishmentSelectionItem {
  return {
    productId: row.productId,
    code: row.code,
    name: row.name,
    currentStock: row.currentStock,
    onOrderQuantity: row.onOrderQuantity,
    targetStock: row.targetStock,
    remainingToOrder: row.remainingToOrder,
  }
}

export function sumRemainingToOrder(items: ZadReplenishmentSelectionItem[]): number {
  return items.reduce((total, item) => total + (Number.isFinite(item.remainingToOrder) ? item.remainingToOrder : 0), 0)
}

export type ZadReplenishmentFieldErrors = Partial<
  Record<'minimumStock' | 'reorderPoint' | 'targetStock', string>
>

export function parseZadThresholdInteger(
  value: string
): {ok: true; value: number} | {ok: false; errorId: string} {
  const trimmed = String(value || '').trim()
  if (!trimmed) return {ok: false, errorId: 'ZAD.REPLENISHMENT.THRESHOLD_REQUIRED'}
  if (!/^\d+$/.test(trimmed)) {
    if (trimmed.includes('.') || trimmed.includes(',')) {
      return {ok: false, errorId: 'ZAD.REPLENISHMENT.THRESHOLD_INTEGER'}
    }
    if (trimmed.startsWith('-')) return {ok: false, errorId: 'ZAD.REPLENISHMENT.THRESHOLD_NEGATIVE'}
    return {ok: false, errorId: 'ZAD.REPLENISHMENT.THRESHOLD_INTEGER'}
  }
  const numeric = Number(trimmed)
  if (!Number.isInteger(numeric)) return {ok: false, errorId: 'ZAD.REPLENISHMENT.THRESHOLD_INTEGER'}
  if (numeric < 0) return {ok: false, errorId: 'ZAD.REPLENISHMENT.THRESHOLD_NEGATIVE'}
  return {ok: true, value: numeric}
}

export function validateZadReplenishmentSettings(input: {
  isEnabled: boolean
  minimumStock: string
  reorderPoint: string
  targetStock: string
}):
  | {ok: true; payload: ZadReplenishmentSettingsPayload}
  | {ok: false; fieldErrors: ZadReplenishmentFieldErrors; orderErrorId?: string} {
  if (!input.isEnabled) {
    return {ok: true, payload: {isEnabled: false}}
  }
  const minimum = parseZadThresholdInteger(input.minimumStock)
  const reorder = parseZadThresholdInteger(input.reorderPoint)
  const target = parseZadThresholdInteger(input.targetStock)
  const fieldErrors: ZadReplenishmentFieldErrors = {}
  if (!minimum.ok) fieldErrors.minimumStock = minimum.errorId
  if (!reorder.ok) fieldErrors.reorderPoint = reorder.errorId
  if (!target.ok) fieldErrors.targetStock = target.errorId
  if (Object.keys(fieldErrors).length) {
    return {ok: false, fieldErrors}
  }
  if (!minimum.ok || !reorder.ok || !target.ok) {
    return {ok: false, fieldErrors}
  }
  if (!(minimum.value <= reorder.value && reorder.value <= target.value)) {
    return {
      ok: false,
      fieldErrors: {},
      orderErrorId: 'ZAD.REPLENISHMENT.THRESHOLD_ORDER',
    }
  }
  return {
    ok: true,
    payload: {
      isEnabled: true,
      minimumStock: minimum.value,
      reorderPoint: reorder.value,
      targetStock: target.value,
    },
  }
}

export function emptyReplenishmentSummary(): ZadReplenishmentSummary {
  return emptySummary()
}
